import { useState, useRef, useEffect } from 'react';

// ==================== 定数定義 ====================
// GAS WebアプリURL（送信先）
// 2026-07-16 リードDB統一: 旧LP共通DB(AKfycbzqxn…)→gas-questionnaire経由でAIシート「LP完結リード」へ集約
const GAS_ENDPOINT = "https://script.google.com/macros/s/AKfycbwMIUr0pXSiubpQ4w7mYoO8WE9RTanN2b1ZAcwPiqky0dh4K7oPvivWlo8asOBPuJPi/exec";

/**
 * このLPで収集するフォーム項目名の配列
 * 配列に含まれない項目は自動的に空文字("")で送信される
 * 全9項目: qualification, birth_year, birth_month, birth_day, name, phone, prefecture, work_style, transfer_timing
 */
const FORM_FIELDS = ["qualification", "birth_year", "birth_month", "birth_day", "name", "phone", "prefecture"];

// 都道府県リスト（関東優先）
const PREFECTURES = [
  "東京都", "神奈川県", "埼玉県", "千葉県", "茨城県", "栃木県", "群馬県",
  "北海道", "青森県", "岩手県", "宮城県", "秋田県", "山形県", "福島県",
  "新潟県", "富山県", "石川県", "福井県", "山梨県", "長野県", "岐阜県",
  "静岡県", "愛知県", "三重県", "滋賀県", "京都府", "大阪府", "兵庫県",
  "奈良県", "和歌山県", "鳥取県", "島根県", "岡山県", "広島県", "山口県",
  "徳島県", "香川県", "愛媛県", "高知県", "福岡県", "佐賀県", "長崎県",
  "熊本県", "大分県", "宮崎県", "鹿児島県", "沖縄県"
];

// ==================== ID生成・取得関数 ====================

/**
 * visitor_id の生成・管理
 * - localStorage から "lp_common_visitor_id" を取得
 * - 存在すればそれを返す（全LP横断で同一ユーザーを識別）
 * - 存在しなければ UUID v4 を生成し、"vis_" プレフィックスを付けて保存・返却
 */
function getOrCreateVisitorId(): string {
  const key = 'lp_common_visitor_id';
  let visitorId = localStorage.getItem(key);

  if (!visitorId) {
    // UUID v4 生成
    const uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
    visitorId = `vis_${uuid}`;
    localStorage.setItem(key, visitorId);
  }

  return visitorId;
}

/**
 * conversion_id の生成（フォーム送信時に毎回新規生成・使い捨て）
 * 形式: cvid_{YYYYMMDD}_{ランダム英数字8桁}
 * 例: cvid_20250601_a1b2c3d4
 */
function generateConversionId(): string {
  const now = new Date();
  const yyyymmdd =
    now.getFullYear().toString() +
    (now.getMonth() + 1).toString().padStart(2, '0') +
    now.getDate().toString().padStart(2, '0');

  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let random = '';
  for (let i = 0; i < 8; i++) {
    random += chars.charAt(Math.floor(Math.random() * chars.length));
  }

  return `cvid_${yyyymmdd}_${random}`;
}

/**
 * UTMパラメータ取得関数
 * ページ表示時にsessionStorageへ保存済みの値を読み取る
 * （ユーザーがページ内でURLが変わった場合に備えて、最初のアクセス時の値を保持）
 */
function getUTMParams() {
  return {
    utm_source: sessionStorage.getItem('utm_source') || '',
    utm_medium: sessionStorage.getItem('utm_medium') || '',
    utm_campaign: sessionStorage.getItem('utm_campaign') || '',
    utm_term: sessionStorage.getItem('utm_term') || '',
    utm_content: sessionStorage.getItem('utm_content') || '',
  };
}

/**
 * アフィリエイトパラメータ取得関数
 * ページ表示時にsessionStorageへ保存済みの値を読み取る
 */
function getAffiliateParams() {
  return {
    aff_company: sessionStorage.getItem('aff_company') || '',
    aff_id: sessionStorage.getItem('aff_id') || '',
    click_id: sessionStorage.getItem('click_id') || '',
  };
}

/**
 * lp_id の取得
 * ページのURLから自動取得（クエリパラメータを除いたorigin+pathname）
 * 例: "https://lp.nijikango.co.jp/"
 */
function getLpId(): string {
  return window.location.origin + window.location.pathname;
}

/**
 * 電話番号の正規化
 * ハイフン・スペースを除去して数字のみにする
 * 例: "090-1234-5678" → "09012345678"
 */
function normalizePhone(phone: string): string {
  return phone.replace(/[-\s]/g, '');
}

// ==================== GA4・LINE Tag ヘルパー ====================

/** GA4イベント送信ヘルパー（多重発火防止付き） */
const sendGA4Event = (eventKey: string, eventType: 'click' | 'view' = 'click') => {
  if (typeof window !== 'undefined' && typeof (window as any).gtag === 'function') {
    const eventName = `nijikango03_${eventType}_${eventKey}`;
    const sessionKey = `ga4_${eventName}`;

    if (!sessionStorage.getItem(sessionKey)) {
      (window as any).gtag('event', eventName, {
        page_path: window.location.pathname,
        page_title: document.title,
        ga_key: eventKey,
        ga_type: eventType,
        timestamp: new Date().toISOString(),
      });
      sessionStorage.setItem(sessionKey, 'sent');
    }
  }
};

/** LINE Tag コンバージョン送信ヘルパー（個人情報は渡さない） */
const sendLINEConversion = () => {
  if (typeof window !== 'undefined' && typeof (window as any)._lt === 'function') {
    (window as any)._lt('send', 'cv', {
      type: 'Conversion',
    }, ['e12bd2c2-7226-450e-94d5-04c55b56ecea']);
  }
};

// ==================== メインコンポーネント ====================

export default function HomePage() {
  const [selectedQualifications, setSelectedQualifications] = useState<string[]>([]);
  const [currentStep, setCurrentStep] = useState(1);
  const [ctaSelectedQualifications, setCtaSelectedQualifications] = useState<string[]>([]);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // フォーム入力値
  const [formBirthYear, setFormBirthYear] = useState('');
  const [formBirthMonth, setFormBirthMonth] = useState('');
  const [formBirthDay, setFormBirthDay] = useState('');
  const [formName, setFormName] = useState('');
  const [formPhone, setFormPhone] = useState('');
  const [formPrefecture, setFormPrefecture] = useState('');

  const formRef = useRef<HTMLFormElement>(null);

  /**
   * ページ表示時の初期化処理（DOMContentLoaded相当）
   * 1. visitor_id を即座に生成・localStorageへ保存
   * 2. URLのUTM・アフィリエイトパラメータをsessionStorageへ保存
   *    （フォーム送信時はsessionStorageから読み取る）
   */
  useEffect(() => {
    // 【機能1】visitor_id を即座に生成・保存（再訪問時は既存IDを再利用）
    getOrCreateVisitorId();

    // UTM・アフィリエイトパラメータをsessionStorageに保存
    // ※ 既に保存済みの場合は上書きしない（最初のランディング時の値を保持）
    const params = new URLSearchParams(window.location.search);

    const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
    utmKeys.forEach((key) => {
      const value = params.get(key);
      if (value && !sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, value);
      }
    });

    const affKeys = ['aff_company', 'aff_id', 'click_id'];
    affKeys.forEach((key) => {
      const value = params.get(key);
      if (value && !sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, value);
      }
    });
  }, []);

  // Intersection Observer for view events
  useEffect(() => {
    const observerOptions = { threshold: 0.5, rootMargin: '0px' };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const sectionKey = entry.target.getAttribute('data-ga-section');
          if (sectionKey) sendGA4Event(sectionKey, 'view');
        }
      });
    }, observerOptions);

    const sections = document.querySelectorAll('[data-ga-section]');
    sections.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  // Click event listeners
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const clickElement = target.closest('[data-ga-key]') as HTMLElement;
      if (clickElement) {
        const gaKey = clickElement.getAttribute('data-ga-key');
        if (gaKey) sendGA4Event(gaKey, 'click');
      }
    };

    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  const handleQualificationChange = (value: string) => {
    setSelectedQualifications((prev) =>
      prev.includes(value) ? prev.filter((q) => q !== value) : [...prev, value]
    );
  };

  const handleCtaQualificationChange = (value: string) => {
    setCtaSelectedQualifications((prev) =>
      prev.includes(value) ? prev.filter((q) => q !== value) : [...prev, value]
    );
  };

  const handleNextStep = () => {
    if (selectedQualifications.length > 0) setCurrentStep(2);
  };

  const handleStep2Next = () => {
    if (formBirthYear && formBirthMonth && formBirthDay) setCurrentStep(3);
  };

  const handleStep3Next = () => {
    if (formName.trim() && formPrefecture) setCurrentStep(4);
  };

  /**
   * フォーム送信処理
   * 1. 電話番号バリデーション（11桁）
   * 2. 【機能2】conversion_id を新規生成（送信のたびに変わる・使い捨て）
   * 3. visitor_id・lp_id・UTM・アフィリエイトパラメータを取得
   * 4. 【機能3】GAS WebアプリURLへJSONをPOST送信
   * 5. 成功時：LINE Tagコンバージョン発火・フォームリセット
   */
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const normalizedPhone = normalizePhone(formPhone);

    // 電話番号バリデーション（11桁のみ許可）
    if (normalizedPhone.length !== 11) {
      alert('電話番号は11桁で入力してください（例：09012345678）');
      return;
    }

    setIsSubmitting(true);

    try {
      // ID生成・取得
      const visitorId = getOrCreateVisitorId();           // localStorage から取得（既存IDを再利用）
      const conversionId = generateConversionId();        // 送信のたびに新規生成（使い捨て）
      const lpId = getLpId();                             // URLから自動取得
      const utmParams = getUTMParams();                   // sessionStorageから読み取り
      const affParams = getAffiliateParams();             // sessionStorageから読み取り

      // 全9項目の値を定義（FORM_FIELDSに含まれない項目は空文字で送信）
      const allFieldValues: Record<string, string> = {
        qualification: selectedQualifications.join(', '), // 複数選択をカンマ区切りで結合
        birth_year: formBirthYear,
        birth_month: formBirthMonth,
        birth_day: formBirthDay,
        name: formName,
        phone: normalizedPhone,                           // ハイフン除去済みの数字のみ
        prefecture: formPrefecture,
        work_style: '',
        transfer_timing: '',
      };

      // FORM_FIELDSに含まれない項目は空文字に統一
      const personalInfo: Record<string, string> = {};
      [
        'qualification', 'birth_year', 'birth_month', 'birth_day',
        'name', 'phone', 'prefecture', 'work_style', 'transfer_timing'
      ].forEach((field) => {
        personalInfo[field] = FORM_FIELDS.includes(field) ? allFieldValues[field] : '';
      });

      // 送信JSONの構築
      const payload = {
        // === ID系（自動生成・自動取得） ===
        visitor_id: visitorId,
        conversion_id: conversionId,
        lp_id: lpId,

        // === 個人情報（フォーム入力値） ===
        ...personalInfo,

        // === マーケティング情報（sessionStorageから自動取得） ===
        ...utmParams,
        ...affParams,
        referrer: document.referrer || '',
        landing_page: window.location.href,

        // === メタ情報（自動取得） ===
        submitted_at: new Date().toISOString(),
        user_agent: navigator.userAgent,
      };

      // GASへPOST送信（no-corsモード：GAS側でCORSヘッダーが設定されていない場合に対応）
      await fetch(GAS_ENDPOINT, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      // no-corsモードではレスポンスがopaqueになるため、fetch成功＝送信成功とみなす
      // ※ 個人情報（phone等）はDBにのみ保存し、LINE Tagにはconversion_idのみ渡す
      sendLINEConversion();
      setSubmitStatus('success');

      // フォームリセット
      setSelectedQualifications([]);
      setFormBirthYear('');
      setFormBirthMonth('');
      setFormBirthDay('');
      setFormName('');
      setFormPhone('');
      setFormPrefecture('');
      setCurrentStep(1);

      setTimeout(() => setSubmitStatus('idle'), 5000);
    } catch (error) {
      console.error('送信エラー:', error);
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus('idle'), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCtaNextStep = () => {
    if (ctaSelectedQualifications.length > 0) {
      setSelectedQualifications(ctaSelectedQualifications);
      setCurrentStep(2);
      const heroForm = document.getElementById('hero-form');
      if (heroForm) heroForm.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  // 年の選択肢（1960〜2006）
  const years = Array.from({ length: 2006 - 1960 + 1 }, (_, i) => 1960 + i);
  const months = Array.from({ length: 12 }, (_, i) => i + 1);
  const days = Array.from({ length: 31 }, (_, i) => i + 1);

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50 via-blue-50 to-pink-50" style={{ fontFamily: 'Arial, sans-serif' }}>
      {/* Submit Status Toast */}
      {submitStatus !== 'idle' && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 animate-fade-in">
          <div className={`px-6 py-3 rounded-xl shadow-lg text-white text-sm font-bold ${
            submitStatus === 'success' ? 'bg-green-500' : 'bg-red-500'
          }`}>
            {submitStatus === 'success' ? '送信が完了しました。担当者より連絡させていただきます。' : '送信に失敗しました。もう一度お試しください。'}
          </div>
        </div>
      )}

      {/* Header */}
      <header className="bg-white shadow-sm" data-ga-section="header">
        <div className="px-4 py-3 flex justify-between items-center">
          <a 
            href="https://nijikango.co.jp/" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="cursor-pointer"
            data-ga-key="click_log"
          >
            <img 
              src="https://static.readdy.ai/image/2eeb7004bec3dab6605078c5e30f883b/57fde759c3b0dccef11bbd387050424f.jpeg" 
              alt="ニジ看護転職" 
              className="h-7"
            />
          </a>
          <div className="text-[10px] text-gray-500">
            厚生労働大臣許可証13-ユ-317878
          </div>
        </div>
      </header>

      <main>
        {/* Hero Section */}
        <section className="relative px-4 pt-8 pb-6 overflow-hidden" data-ga-section="fv">
          {/* Background Image - Upper portion only */}
          <div className="absolute inset-x-0 top-0 h-[60%] z-0">
            <img 
              src="https://public.readdy.ai/ai/img_res/4353b692-4918-42ba-a9c1-396abdb16b19.png" 
              alt="看護師アドバイザー" 
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-white/80 via-white/60 to-white/95"></div>
          </div>

          <div className="relative z-10">
            <div className="mb-6 mt-44">
              <h1 className="text-2xl font-bold text-gray-800 leading-tight">
                <span className="block">看護師の転職なら</span>
                <span className="block mt-1">ニジ看護転職</span>
                <span className="block mt-2 text-pink-500">
                  ひとりひとりを<span className="bg-yellow-200 px-1">親身にサポート</span>
                </span>
              </h1>
            </div>

            {/* Feature Circles - 2 in a row on mobile */}
            <div className="flex justify-center gap-4 mb-6">
              <div className="w-[140px] h-[140px] bg-gradient-to-br from-pink-400 to-pink-500 rounded-full flex items-center justify-center text-white shadow-md flex-shrink-0">
                <div className="text-center px-2">
                  <div className="font-bold leading-tight">
                    <span className="bg-white text-pink-500 px-2 rounded text-[14px]">元看護師</span>
                    <span className="block text-[13px] my-0.5">✕</span>
                    <span className="block text-[15px]">女性アドバイザー</span>
                  </div>
                  <div className="text-[12px] mt-1">が親身に対応</div>
                </div>
              </div>

              <div className="w-[140px] h-[140px] bg-gradient-to-br from-pink-400 to-pink-500 rounded-full flex items-center justify-center text-white shadow-md flex-shrink-0">
                <div className="text-center px-2">
                  <div className="font-bold leading-tight">
                    <span className="bg-white text-pink-500 px-2 rounded text-[14px] block">非公開</span>
                    <span className="bg-white text-pink-500 px-2 rounded text-[14px] block mt-1">高待遇求人</span>
                    <span className="block mt-1 text-[15px]">多数紹介</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Survey Form */}
            <div className="bg-white rounded-2xl shadow-xl p-5" data-ga-section="main_form">
              <div className="bg-gradient-to-r from-pink-500 to-red-400 text-white rounded-xl p-3 mb-4 flex items-center">
                <div className="bg-white rounded-full w-10 h-10 flex items-center justify-center mr-3 flex-shrink-0">
                  <i className="ri-customer-service-2-fill text-pink-500 text-lg"></i>
                </div>
                <div className="text-base leading-snug font-bold">
                  簡単な質問に答えるだけで<br />
                  あなたにぴったりの求人をご紹介!
                </div>
              </div>

              <form id="hero-form" ref={formRef} onSubmit={handleSubmit} data-readdy-form>
                {/* Step 1: Qualifications */}
                <div className={currentStep === 1 ? '' : 'hidden'}>
                  <div className="text-center mb-4">
                    <h3 className="text-base font-bold text-gray-800">どんな資格をお持ちですか?</h3>
                    <p className="text-xs text-gray-500 mt-1">複数選択可能</p>
                  </div>

                  <div className="grid grid-cols-2 gap-3 mb-6">
                    {['正看護師', '准看護師', '保健師', '助産師'].map((qual) => (
                      <label key={qual} className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          value={qual}
                          checked={selectedQualifications.includes(qual)}
                          onChange={() => handleQualificationChange(qual)}
                          className="hidden"
                        />
                        <span className={`w-5 h-5 border-2 rounded mr-2 flex items-center justify-center flex-shrink-0 ${
                          selectedQualifications.includes(qual) ? 'bg-pink-500 border-pink-500' : 'border-gray-300'
                        }`}>
                          {selectedQualifications.includes(qual) && (
                            <i className="ri-check-line text-white text-xs"></i>
                          )}
                        </span>
                        <span className="text-gray-700 text-lg">{qual}</span>
                      </label>
                    ))}
                  </div>

                  <button
                    type="button"
                    onClick={handleNextStep}
                    disabled={selectedQualifications.length === 0}
                    className="w-full bg-gradient-to-r from-pink-500 to-red-400 text-white py-3 rounded-full font-bold text-base hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                    data-ga-key="click_1_1"
                  >
                    <span className="block text-sm">完全無料!</span>
                    <span>次のステップへ</span>
                  </button>
                </div>

                {/* Step 2: Birth Date */}
                <div className={currentStep === 2 ? '' : 'hidden'}>
                  <div className="text-center mb-4">
                    <h3 className="text-base font-bold text-gray-800">生年月日を教えてください</h3>
                  </div>
                  <div className="grid grid-cols-3 gap-2 mb-4">
                    <select
                      value={formBirthYear}
                      onChange={(e) => setFormBirthYear(e.target.value)}
                      className="border-2 border-gray-300 rounded-lg px-2 py-3 text-sm focus:border-pink-500 focus:outline-none"
                    >
                      <option value="">西暦</option>
                      {years.map(year => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>
                    <select
                      value={formBirthMonth}
                      onChange={(e) => setFormBirthMonth(e.target.value)}
                      className="border-2 border-gray-300 rounded-lg px-2 py-3 text-sm focus:border-pink-500 focus:outline-none"
                    >
                      <option value="">月</option>
                      {months.map(month => (
                        <option key={month} value={month}>{month}</option>
                      ))}
                    </select>
                    <select
                      value={formBirthDay}
                      onChange={(e) => setFormBirthDay(e.target.value)}
                      className="border-2 border-gray-300 rounded-lg px-2 py-3 text-sm focus:border-pink-500 focus:outline-none"
                    >
                      <option value="">日</option>
                      {days.map(day => (
                        <option key={day} value={day}>{day}</option>
                      ))}
                    </select>
                  </div>
                  <button
                    type="button"
                    onClick={handleStep2Next}
                    disabled={!formBirthYear || !formBirthMonth || !formBirthDay}
                    className="w-full bg-gradient-to-r from-pink-500 to-red-400 text-white py-3 rounded-full font-bold text-base hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                  >
                    次へ
                  </button>
                </div>

                {/* Step 3: Name & Prefecture */}
                <div className={currentStep === 3 ? '' : 'hidden'}>
                  <div className="text-center mb-4">
                    <h3 className="text-base font-bold text-gray-800">氏名とお住まいの都道府県を教えてください</h3>
                    <p className="text-xs text-gray-500 mt-1">氏名は仮名でもOKです</p>
                  </div>
                  <input
                    type="text"
                    name="name"
                    value={formName}
                    onChange={(e) => setFormName(e.target.value)}
                    placeholder="山田 花子"
                    className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 mb-3 text-sm focus:border-pink-500 focus:outline-none"
                  />
                  <select
                    value={formPrefecture}
                    onChange={(e) => setFormPrefecture(e.target.value)}
                    className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 mb-4 text-sm focus:border-pink-500 focus:outline-none"
                  >
                    <option value="">都道府県を選択</option>
                    {PREFECTURES.map(pref => (
                      <option key={pref} value={pref}>{pref}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={handleStep3Next}
                    disabled={!formName.trim() || !formPrefecture}
                    className="w-full bg-gradient-to-r from-pink-500 to-red-400 text-white py-3 rounded-full font-bold text-base hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                  >
                    次へ
                  </button>
                </div>

                {/* Step 4: Phone */}
                <div className={currentStep === 4 ? '' : 'hidden'}>
                  <div className="text-center mb-4">
                    <h3 className="text-base font-bold text-gray-800">電話番号を教えてください</h3>
                    <p className="text-xs text-gray-500 mt-1">11桁の数字を入力してください</p>
                  </div>
                  <input
                    type="tel"
                    name="phone"
                    value={formPhone}
                    onChange={(e) => setFormPhone(e.target.value)}
                    placeholder="09012345678"
                    maxLength={13}
                    className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 mb-4 text-sm focus:border-pink-500 focus:outline-none"
                  />
                  <button
                    type="submit"
                    disabled={!formPhone.trim() || isSubmitting}
                    className="w-full bg-gradient-to-r from-pink-500 to-red-400 text-white py-3 rounded-full font-bold text-base hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                    data-ga-key="click_1_2"
                  >
                    {isSubmitting ? (
                      <span>送信中...</span>
                    ) : (
                      <>
                        <span className="block text-sm">完全無料!</span>
                        <span>送信する</span>
                      </>
                    )}
                  </button>
                </div>

                <div className="flex justify-center gap-1.5 mt-4">
                  {[1, 2, 3, 4].map((step) => (
                    <span
                      key={step}
                      className={`w-2 h-2 rounded-full ${
                        step <= currentStep ? 'bg-pink-500' : 'bg-gray-300'
                      }`}
                    ></span>
                  ))}
                </div>
              </form>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-12 px-4 bg-white" data-ga-section="benefit">
          <div className="max-w-lg mx-auto">
            <h2 className="text-xl font-bold text-center text-pink-500 mb-10">
              ニジ看護転職が選ばれる理由
            </h2>

            <div className="space-y-8">
              {/* Reason 1 */}
              <div className="bg-pink-50 rounded-2xl p-5">
                <div className="flex flex-col items-center gap-4">
                  <div className="flex-shrink-0">
                    <img 
                      src="https://lp.nijikango.co.jp/assets/reason_1-1EZk5GC5.svg" 
                      alt="元看護師アドバイザー"
                      className="w-24 h-24"
                    />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-base font-bold text-gray-800 mb-4 text-center">
                      元看護師アドバイザーによる親身サポート
                    </h3>
                    <div className="space-y-3">
                      <div className="flex items-start">
                        <i className="ri-check-line text-pink-500 text-base mr-2 mt-0.5 flex-shrink-0"></i>
                        <p className="text-gray-700 text-sm">
                          <strong>現場を知る女性アドバイザー</strong>が転職活動をフルサポート
                        </p>
                      </div>
                      <div className="flex items-start">
                        <i className="ri-check-line text-pink-500 text-base mr-2 mt-0.5 flex-shrink-0"></i>
                        <p className="text-gray-700 text-sm">
                          キャリア相談から面接対策・条件交渉まで、あなたに合わせたアドバイスが可能
                        </p>
                      </div>
                      <div className="flex items-start">
                        <i className="ri-check-line text-pink-500 text-base mr-2 mt-0.5 flex-shrink-0"></i>
                        <p className="text-gray-700 text-sm">
                          「ブランクがあって不安...」「職場の雰囲気を知りたい」など、看護師の現場を熟知しているからこそ安心して任せられます
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Reason 2 */}
              <div className="bg-pink-50 rounded-2xl p-5">
                <div className="flex flex-col items-center gap-4">
                  <div className="flex-shrink-0">
                    <img 
                      src="https://lp.nijikango.co.jp/assets/reason_2-B0EDeZFN.svg" 
                      alt="LINE相談"
                      className="w-24 h-24"
                    />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-base font-bold text-gray-800 mb-4 text-center">
                      LINEで気軽に相談 完全無料
                    </h3>
                    <div className="space-y-3 mb-5">
                      <div className="flex items-start">
                        <i className="ri-check-line text-pink-500 text-base mr-2 mt-0.5 flex-shrink-0"></i>
                        <p className="text-gray-700 text-sm">
                          登録や利用に費用は一切かかりません
                        </p>
                      </div>
                      <div className="flex items-start">
                        <i className="ri-check-line text-pink-500 text-base mr-2 mt-0.5 flex-shrink-0"></i>
                        <p className="text-gray-700 text-sm">
                          <strong>LINE相談OK</strong>で、勤務の合間や自宅でも手軽に情報交換可能
                        </p>
                      </div>
                      <div className="flex items-start">
                        <i className="ri-check-line text-pink-500 text-base mr-2 mt-0.5 flex-shrink-0"></i>
                        <p className="text-gray-700 text-sm">
                          電話対応が難しい方でもチャットでスムーズに進められるので、忙しい現場でもストレスフリー
                        </p>
                      </div>
                    </div>
                    <div className="text-center">
                      <a 
                        href="https://page.line.me/266jzhpa?oat_content=url&openQrModal=true" 
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-block bg-gradient-to-r from-green-400 to-green-500 text-white px-8 py-3 rounded-full font-bold text-sm hover:shadow-lg transition-all whitespace-nowrap cursor-pointer"
                        data-ga-key="benefit_cta"
                      >
                        今すぐ無料で相談
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Reason 3 */}
              <div className="bg-pink-50 rounded-2xl p-5">
                <div className="flex flex-col items-center gap-4">
                  <div className="flex-shrink-0">
                    <img 
                      src="https://lp.nijikango.co.jp/assets/reason_3-Bc64JkMt.svg" 
                      alt="徹底サポート"
                      className="w-24 h-24"
                    />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-base font-bold text-gray-800 mb-4 text-center">
                      希望の転職先が見つかるまで、徹底的にサポート!
                    </h3>
                    <div className="space-y-3">
                      <div className="flex items-start">
                        <i className="ri-check-line text-pink-500 text-base mr-2 mt-0.5 flex-shrink-0"></i>
                        <p className="text-gray-700 text-sm">
                          理想の転職先が見つからない場合も、私たちが<strong>徹底的にリサーチ・直接交渉</strong>し、あなただけの求人を見つけます
                        </p>
                      </div>
                      <div className="flex items-start">
                        <i className="ri-check-line text-pink-500 text-base mr-2 mt-0.5 flex-shrink-0"></i>
                        <p className="text-gray-700 text-sm">
                          病院・クリニック・介護施設・企業の産業看護師など幅広い求人を取り扱っています。地域専任のアドバイザーが各エリアで最大級の求人数からあなたの希望に合った求人をお探しします。
                        </p>
                      </div>
                      <div className="flex items-start">
                        <i className="ri-check-line text-pink-500 text-base mr-2 mt-0.5 flex-shrink-0"></i>
                        <p className="text-gray-700 text-sm">
                          非公開の高待遇求人も多数紹介ができます。
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-10 px-4 bg-gradient-to-b from-pink-50 to-blue-50" data-ga-section="cta_section">
          <div className="max-w-lg mx-auto text-center">
            <h2 className="text-lg font-bold text-gray-800 mb-6">
              <span className="text-pink-500">\</span> まずはアンケートに回答してください <span className="text-pink-500">/</span>
            </h2>

            <div className="bg-white rounded-2xl shadow-xl p-5 mb-6">
              <div className="bg-gradient-to-r from-pink-500 to-red-400 text-white rounded-xl p-3 mb-4 flex items-center justify-center">
                <div className="bg-white rounded-full w-10 h-10 flex items-center justify-center mr-3 flex-shrink-0">
                  <i className="ri-customer-service-2-fill text-pink-500 text-lg"></i>
                </div>
                <div className="text-base leading-snug font-bold">
                  簡単な質問に答えるだけで<br />
                  あなたにぴったりの求人をご紹介!
                </div>
              </div>

              <div className="text-center mb-4">
                <h3 className="text-base font-bold text-gray-800">どんな資格をお持ちですか?</h3>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-6">
                {['正看護師', '准看護師', '保健師', '助産師'].map((qual) => (
                  <label key={`cta-${qual}`} className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      value={qual}
                      checked={ctaSelectedQualifications.includes(qual)}
                      onChange={() => handleCtaQualificationChange(qual)}
                      className="hidden"
                    />
                    <span className={`w-5 h-5 border-2 rounded mr-2 flex items-center justify-center flex-shrink-0 ${
                      ctaSelectedQualifications.includes(qual) ? 'bg-pink-500 border-pink-500' : 'border-gray-300'
                    }`}>
                      {ctaSelectedQualifications.includes(qual) && (
                        <i className="ri-check-line text-white text-xs"></i>
                      )}
                    </span>
                    <span className="text-gray-700 text-lg">{qual}</span>
                  </label>
                ))}
              </div>

              <button
                onClick={handleCtaNextStep}
                className="w-full bg-gradient-to-r from-pink-500 to-red-400 text-white py-3 rounded-full font-bold text-base hover:shadow-lg transition-all whitespace-nowrap cursor-pointer"
                data-ga-key="click_2_1"
              >
                <span className="block text-sm">完全無料!</span>
                <span>次のステップへ</span>
              </button>

              <div className="flex justify-center gap-1.5 mt-4">
                {[1, 2, 3, 4].map((step) => (
                  <span key={step} className="w-2 h-2 rounded-full bg-gray-300"></span>
                ))}
              </div>
            </div>

            <h3 className="text-base font-bold text-gray-800 mb-4">
              <span className="text-pink-500">\</span> 情報収集だけでもOKです <span className="text-pink-500">/</span>
            </h3>

            <a 
              href="#hero-form" 
              className="inline-block bg-gradient-to-r from-green-400 to-green-500 text-white px-10 py-3 rounded-full font-bold text-sm hover:shadow-lg transition-all whitespace-nowrap cursor-pointer"
              data-ga-key="cta_bottom"
            >
              今すぐ無料で相談
            </a>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-12 px-4 bg-white" data-ga-section="faq">
          <div className="max-w-lg mx-auto">
            <h2 className="text-xl font-bold text-center text-pink-500 mb-10">
              よくある質問
            </h2>

            <div className="space-y-4">
              {[
                {
                  q: '本当に転職は無料ですか?',
                  a: 'はい、当サービスの利用に費用は一切かかりません。採用が決まった際は勤務先から紹介手数料を頂くモデルのため、求職者様の自己負担はゼロです。'
                },
                {
                  q: '在職中なので、現在の職場にバレないか心配です。',
                  a: 'ご安心ください。登録内容や相談内容が現在の勤務先など第三者に知られることは決してありません。秘密厳守でサポートいたします。'
                },
                {
                  q: 'まだ転職するか決めていなくても相談できますか?',
                  a: 'もちろんです!情報収集だけのご相談も大歓迎です。無理に転職を斡旋することは一切ありませんので、「とりあえず話を聞いてみたい」という段階でもお気軽にご利用ください。'
                },
                {
                  q: '忙しくて電話に出れないのですが…',
                  a: 'ご希望の連絡手段や時間帯を選べます。LINEやメールでの連絡も可能ですので、勤務中は難しい方も隙間時間にやり取りできます。実際、当サービスではLINE連絡を希望される看護師さん多くいらっしゃいます。'
                },
                {
                  q: '紹介してもらえる求人はどんなものがありますか?',
                  a: '病院(一般病院から大学病院まで)、クリニック、介護施設、企業の産業看護師など幅広い求人を取り扱っています。地域専任のアドバイザーが各エリアで最大級の求人数からあなたの希望に合った求人をお探しします。'
                }
              ].map((faq, index) => (
                <div key={index} className="bg-pink-50 rounded-xl p-4">
                  <div className="flex items-start mb-3">
                    <span className="bg-pink-500 text-white font-bold px-2 py-0.5 rounded-full text-xs mr-3 flex-shrink-0 mt-0.5">
                      Q{index + 1}
                    </span>
                    <h3 className="font-bold text-gray-800 text-sm">{faq.q}</h3>
                  </div>
                  <div className="flex items-start pl-9">
                    <span className="text-pink-500 font-bold mr-3 flex-shrink-0 text-sm">A</span>
                    <p className="text-gray-700 text-sm leading-relaxed">{faq.a}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-10 px-4 bg-gradient-to-b from-blue-50 to-pink-50">
          <div className="max-w-lg mx-auto text-center">
            <a 
              href="#hero-form" 
              className="inline-block bg-gradient-to-r from-pink-500 to-red-400 text-white px-10 py-4 rounded-full font-bold text-base hover:shadow-lg transition-all whitespace-nowrap cursor-pointer"
              data-ga-key="contact_cta"
            >
              今すぐ無料で相談
            </a>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-slate-100 text-gray-700 py-8 px-4" data-ga-section="footer">
        <div className="max-w-lg mx-auto">
          <div className="flex items-center justify-center gap-6 mb-6">
            <a 
              href="https://nijikango.co.jp/" 
              target="_blank" 
              rel="noopener noreferrer nofollow" 
              className="text-sm hover:text-pink-500 transition-colors cursor-pointer"
              data-ga-key="footer_company"
            >
              運営会社
            </a>
            <a 
              href="https://lp.nijikango.co.jp/privacy-policy" 
              target="_blank" 
              rel="noopener noreferrer nofollow" 
              className="text-sm hover:text-pink-500 transition-colors cursor-pointer"
              data-ga-key="footer_privacy"
            >
              プライバシーポリシー
            </a>
            <a 
              href="https://lp.nijikango.co.jp/terms-of-service" 
              target="_blank" 
              rel="noopener noreferrer nofollow" 
              className="text-sm hover:text-pink-500 transition-colors cursor-pointer"
              data-ga-key="footer_terms"
            >
              利用規約
            </a>
          </div>
          <div className="text-center text-xs text-gray-400 mb-3">
            &copy; 2025 ニジ看護転職
          </div>
          <div className="text-center">
            <a 
              href="https://readdy.ai/?ref=logo" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-[10px] text-gray-400 hover:text-pink-400 transition-colors cursor-pointer"
              data-ga-key="footer_readdy"
            >
              Powered by Readdy
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
